import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { AssetsStatus } from 'enums/AssetsStatus';
import { queryClient } from 'utils/query';
import { buildOrderBy, Filter, PageRequestProps } from 'utils/query_v3';

/**
 * The query key for the asset status hook.
 */
export const assetStatusesQueryKey = 'assets-status';

export type UseAssetStatus = PageRequestProps<
    Readonly<{
        groupIds: string[];
        filterText: string | undefined;
        status: AssetsStatus;
        sort: DataSourceSort;
    }>
>;

/**
 * Get the status of assets, including alarm state, severity, and current value.
 * @see `sdClient.getAssetStatuses`
 */
export function useAssetStatuses({
    groupIds,
    page,
    pageSize: pageLimit,
    filterText,
    status,
    sort,
}: UseAssetStatus) {
    const queryKey = [assetStatusesQueryKey, page, groupIds, filterText, status, sort];
    const queryResult = useQuery({
        queryKey: queryKey,

        queryFn: () => {
            const filter = Filter.and(
                filterText && filterText.length > 0
                    ? Filter.contains('Name', filterText)
                    : undefined,
                status === AssetsStatus.WithIssues
                    ? Filter.neq('ActiveIssueCount', 0)
                    : undefined,
                status === AssetsStatus.WithModels
                    ? Filter.neq('ModelCount', 0)
                    : undefined,
                status === AssetsStatus.WithAlerts
                    ? Filter.or(
                          Filter.neq('CriticalAlarmCount', 0),
                          Filter.neq('AlarmCount', 0),
                          Filter.neq('WarningCount', 0),
                      )
                    : undefined,
            );

            const orderByExpr = buildOrderBy([sort], {
                descNullsLast: ['LastIssueUpdate'],
            });

            return sdClient.getAssetStatuses({
                groupIds,
                filter,
                page,
                pageLimit,
                orderBy: orderByExpr,
            });
        },

        placeholderData: keepPreviousData,
        enabled: groupIds.length > 0,
    });
    return {
        ...queryResult,
        queryKey,
    };
}

export function invalidateAssetStatuses() {
    queryClient.invalidateQueries({
        queryKey: [assetStatusesQueryKey],
    });
}
